import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  Dialog,
  Switch,
  Skeleton,
  CircularProgress,
  Fade,
  Card,
  CardContent,
  Divider,
  Stack,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as MonetizationOnIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  useGetAuthorizeDefinitionEndpointsQuery,
  useGetRolesToEndpointMutation,
  useAssignRoleEndpointMutation,
  useGetRolesForPageQuery,
} from '../../../services/authorization';
import { Role, RolesToEndpointResponse } from '../../../types/authorization.types';
import { UserInfo } from '../services/user';
import { usePermission } from '../../../hooks/usePermission';
import FintechLoader from '../../../components/FintechLoader';
import WaveAnimation from '../../../components/WaveAnimation';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { translateText } from '../../../utils/language';
import { RootState } from '../../../store/store';

const Authorizations: React.FC = () => {
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [expandedMenus, setExpandedMenus] = useState<boolean[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [selectedAction, setSelectedAction] = useState<{ code: string; menu: string; name: string } | null>(null);
  const [endpointToRoleResponse, setEndpointToRoleResponse] = useState<RolesToEndpointResponse[]>([]);
  const [updatingRoles, setUpdatingRoles] = useState(false);
  const [showAssignConfirm, setShowAssignConfirm] = useState(false);

  // Get user info from localStorage (similar to Angular version)
  const getUserInfo = (): UserInfo | null => {
    try {
      const users = JSON.parse(localStorage.getItem('currentUser') || '[]');
      return users.find((x: UserInfo) => x.selectedUserInfo === 1) || null;
    } catch {
      return null;
    }
  };

  const userInfo = getUserInfo();
  const authorizeListCode = 'Corporate.GET.Reading.YetkilendirmeSayfasınaErişim';
  const roleManagementCode = 'Corporate.POST.Writing.İşlemlereRolAtamaYetkisi';

  // API hooks
  const { hasPermission: authorizeList, isLoading: authCheckLoading } = usePermission({
    code: authorizeListCode
  });
  const { hasPermission: canManageRoles, isLoading: roleManagementLoading } = usePermission({
    code: roleManagementCode
  });
  const [getRolesToEndpoint] = useGetRolesToEndpointMutation();
  const [assignRoleEndpoint] = useAssignRoleEndpointMutation();

  const {
    data: menusResponse,
    isLoading: menusLoading,
    refetch: refetchMenus
  } = useGetAuthorizeDefinitionEndpointsQuery(undefined, {
    skip: !authorizeList,
  });

  const {
    data: rolesResponse,
    isLoading: rolesLoading,
    refetch: refetchRolesForPage
  } = useGetRolesForPageQuery(userInfo?.companyId?.toString() || '', {
    skip: !userInfo?.companyId || !authorizeList || authCheckLoading || roleManagementLoading || !canManageRoles,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Initialize expanded state when menus are loaded
  useEffect(() => {
    if (menusResponse?.data) {
      setExpandedMenus(new Array(menusResponse.data.length).fill(false));
    }
  }, [menusResponse]);

  // Update selectedRoles when rolesResponse changes and dialog is open
  useEffect(() => {
    if (!dialogOpen || !rolesResponse?.data) {
      return;
    }

    const endpointRoleIds = new Set((endpointToRoleResponse ?? []).map(role => role.roleId));

    const rolesWithStatus = rolesResponse.data
      .filter(role =>
        role.name.includes('CorporateCompany') &&
        role.name !== 'CorporateCompany.User' &&
        !role.name.includes('SuperAdmin')
      )
      .map(role => ({
        ...role,
        status: endpointRoleIds.has(role.id)
      }));

    setSelectedRoles(rolesWithStatus);
  }, [rolesResponse, dialogOpen, endpointToRoleResponse]);

  useEffect(() => {
    if (!dialogOpen) {
      setShowAssignConfirm(false);
    }
  }, [dialogOpen]);

  const toggleMenu = (index: number) => {
    setExpandedMenus(prev => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  // Function to get icon based on translated text
  const getMenuIcon = (menuName: string) => {
    const translatedName = translateText(menuName, 'tr');

    switch (translatedName) {
      case 'YETKİLENDİRME İŞLEMLERİ':
        return <VpnKeyIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'KART İŞLEMLERİ':
        return <CreditCardIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'FİRMA BİLGİLERİ':
        return <BusinessIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'ONAY/RED İŞLEMLERİ':
        return <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'İŞLEM RAPORLARI':
        return <AssessmentIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'PARA TRANSFER İŞLEMLERİ':
        return <MonetizationOnIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'KULLANICI ROL YETKİLENDİRME':
        return <AdminPanelSettingsIcon sx={{ fontSize: 20, color: 'white' }} />;
      case 'KULLANICI İŞLEMLERİ':
        return <PersonIcon sx={{ fontSize: 20, color: 'white' }} />;
      default:
        return <GroupIcon sx={{ fontSize: 20, color: 'white' }} />;
    }
  };

  const removeLastSegment = (str: string): string => {
    return str.split('.').slice(2).join('.');
  };

  const countSelectedRoles = (): number => {
    return selectedRoles.filter(role => role.status).length;
  };

  const handleShowRoles = async (code: string, menu: string, actionName: string) => {
    if (roleManagementLoading || !canManageRoles) {
      enqueueSnackbar('Rol yönetim yetkiniz bulunmamaktadır.', { variant: 'error' });
      return;
    }

    setSelectedAction({ code, menu, name: actionName });
    setDialogLoading(true);
    setDialogOpen(true);

    try {
      await refetchRolesForPage().unwrap();
      const endpointResult = await getRolesToEndpoint({
        code,
        menu,
        companyId: currentUserState?.companyId || 0,
      }).unwrap();
      setEndpointToRoleResponse(endpointResult.data || []);

    } catch (error) {
      setDialogOpen(false);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles(prev =>
      prev.map(role =>
        role.id === roleId ? { ...role, status: !role.status } : role
      )
    );
  };

  const handleRequestUpdateRoles = () => {
    if (!selectedAction || updatingRoles || roleManagementLoading || !canManageRoles) return;
    setShowAssignConfirm(true);
  };

  const performUpdateRoles = async () => {
    if (!selectedAction || updatingRoles || roleManagementLoading || !canManageRoles) return;
    setUpdatingRoles(true);

    try {
      const selectedRoleNames = selectedRoles
        .filter(role => role.status)
        .map(role => role.name);

      const requestData = {
        code: selectedAction.code,
        menu: selectedAction.menu,
        companyId: currentUserState?.companyId || 0,
        roles: selectedRoleNames,
      };
      await assignRoleEndpoint(requestData).unwrap();
      enqueueSnackbar('Roller başarıyla güncellendi', { variant: 'success' });
      setShowAssignConfirm(false);
      setDialogOpen(false);
      refetchMenus();
    } catch (error) {
      enqueueSnackbar('Roller güncellenirken hata oluştu', { variant: 'error' });
    } finally {
      setUpdatingRoles(false);
    }
  };

  const assignConfirmSelectedLabels = selectedRoles
    .filter((role) => role.status)
    .map((role) => removeLastSegment(role.name));

  if (!authorizeList && !authCheckLoading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Card sx={{
          maxWidth: 500,
          p: 4,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(252, 165, 165, 0.3)',
          textAlign: 'center'
        }}>
          <SecurityIcon sx={{ fontSize: 64, color: '#ef4444', mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
            Erişim Engellendi
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
            Bu sayfaya erişim yetkiniz bulunmamaktadır. Sistem yöneticinizle iletişime geçiniz.
          </Typography>
        </Card>
      </Box>
    );
  }

  if (menusLoading) {
    return (
      <Box sx={{
        minHeight: '100%',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <FintechLoader />
      </Box>
    );
  }

  const menus = menusResponse?.data || [];

  return (
   
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 1 } }}>
 <Dialog
        open={dialogOpen}
        onClose={() =>{
          setEndpointToRoleResponse([]);
          setDialogOpen(false)}}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxHeight: isMobile ? '100%' : '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Dialog Header */}
        <Box sx={{
          flexShrink: 0,
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: 'white',
          p: { xs: 2, sm: 3 }
        }}>
          <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between" width="100%">
            <Stack direction="row" spacing={3} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <GroupIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Rol Atama Yönetimi
                </Typography>
                {selectedAction && (
                  <Typography variant="body1" sx={{ opacity: 0.9, color: "white" }}>
                    {selectedAction.name}
                  </Typography>
                )}
              </Box>
            </Stack>
            <IconButton
              onClick={() => {
                setEndpointToRoleResponse([]);
                setDialogOpen(false);
              }}
              sx={{
                color: 'white',
                flexShrink: 0,
                '&:hover': { background: 'rgba(255,255,255,0.2)' }
              }}
              aria-label="Kapat"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Dialog Content - single scroll (roles list) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: { xs: 2, sm: 3 },
          }}
        >
          {/* Stats Section */}
          <Card
            sx={{
              flexShrink: 0,
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 24, color: 'white' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Seçili Rol Sayısı
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    flexShrink: 0,
                  }}
                >
                  {countSelectedRoles()}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Roles List - the only scrollable area */}
          {dialogLoading ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
              }}
            >
              <FintechLoader />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f5f9',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '3px',
                },
              }}
            >
              <Stack
                spacing={2}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  '& > *': {
                    flexShrink: 0,
                    minWidth: 0,
                  },
                }}
              >
                {selectedRoles.map((role) => (
                  <Card
                    key={role.id}
                    elevation={0}
                    sx={{
                      background: role.status
                        ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)'
                        : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                      border: role.status ? '2px solid #bbf7d0' : '1px solid #e2e8f0',
                      borderRadius: 2,
                      transition: 'all 0.8s ease',
                      width: '100%',
                      minWidth: 0,
                      '&:hover': {
                        transform: 'scale(1.005)',
                        boxShadow: role.status
                          ? '0 12px 32px rgba(34, 197, 94, 0.2)'
                          : '0 12px 32px rgba(0,0,0,0.12)',
                        zIndex: 1,
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              background: role.status
                                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <GroupIcon sx={{ fontSize: 24, color: role.status ? 'white' : '#64748b' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: role.status ? '#166534' : '#1e293b',
                                mb: 0.5,
                              }}
                            >
                              {removeLastSegment(role.name)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: role.status ? '#16a34a' : '#64748b', fontWeight: 500 }}>
                              {role.status ? 'Aktif rol' : 'Pasif rol'}
                            </Typography>
                          </Box>
                        </Stack>

                        <Switch
                          checked={role.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleRoleToggle(role.id);
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#22c55e',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#22c55e',
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: '#e2e8f0',
                            },
                            '& .MuiSwitch-thumb': {
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            },
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {/* Dialog Actions - fixed at bottom */}
        <Box sx={{
          flexShrink: 0,
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          borderTop: '1px solid #e2e8f0'
        }}>
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => setDialogOpen(false)}
              sx={{
                borderColor: '#e2e8f0',
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.9375rem',
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  background: '#f8fafc'
                }
              }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              fullWidth={isMobile}
              onClick={handleRequestUpdateRoles}
              disabled={rolesLoading || updatingRoles}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.9375rem',
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                py: 1.5,
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
                  transform: 'scale(1.005)'
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#64748b',
                  boxShadow: 'none'
                }
              }}
            >
              {rolesLoading || updatingRoles ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} sx={{ color: 'inherit' }} />
                  <span>Güncelleniyor...</span>
                </Stack>
              ) : (
                'Rolleri Güncelle'
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <ConfirmationModal
        isOpen={showAssignConfirm}
        onClose={() => setShowAssignConfirm(false)}
        onConfirm={() => void performUpdateRoles()}
        title="İşlem Onayı"
        confirmButtonText={updatingRoles ? 'Güncelleniyor...' : 'Onayla'}
        closeButtonText="İptal"
        isLoading={updatingRoles}
        optionalWidth="440px"
      >
        {selectedAction && (
          <Typography
            component="p"
            variant="body1"
            id="alert-dialog-description"
            sx={{
              m: 0,
              mb: 1,
              color: 'black',
              lineHeight: 1.65,
              fontWeight: 400,
            }}
          >
            {assignConfirmSelectedLabels.length === 0
              ? 'Hiç rol seçilmedi. Bu yetki için mevcut rol atamalarını kaldırmak istediğinize emin misiniz?'
              : assignConfirmSelectedLabels.length === 1
                ? `${assignConfirmSelectedLabels[0]} rolünü atamak istediğinize emin misiniz?`
                : `${assignConfirmSelectedLabels.join(', ')} rollerini atamak istediğinize emin misiniz?`}
          </Typography>
        )}
      </ConfirmationModal>

        {/* Menu Cards */}
        <Stack spacing={{ xs: 2, sm: 3 }}>
          {menus.map((menu, index) => (
            <WaveAnimation key={index} delay={index * 0.15} duration={0.8}>
              <Card
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                  willChange: 'transform, box-shadow',
                  transition: 'all 0.25s ease-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.005)',
                    boxShadow: '0 25px 60px rgba(59, 130, 246, 0.2)',
                    background: 'rgba(255,255,255,0.95)'
                  }
                }}
              >
                {/* Menu Header */}
                <Box
                  onClick={() => toggleMenu(index)}
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1.5, sm: 2 },
                    cursor: 'pointer',
                    background: expandedMenus[index]
                      ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                      : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    color: expandedMenus[index] ? 'white' : '#1e293b',
                    transition: 'all 0.3s ease !important',
                    '&:hover': {
                      background: expandedMenus[index]
                        ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                        : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)'
                    }
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                      <Box sx={{
                        width: { xs: 40, sm: 44 },
                        height: { xs: 40, sm: 44 },
                        borderRadius: 2,
                        background: expandedMenus[index]
                          ? 'rgba(255,255,255,0.2)'
                          : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                      }}>
                        {getMenuIcon(menu.name)}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: { xs: '0.95rem', sm: '1rem' }
                        }}>
                          {translateText(menu.name, 'tr')}
                        </Typography>
                        <Typography variant="body2" sx={{
                          opacity: 0.8,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                        }}>
                          {menu.actions.length} işlem bulundu
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{
                      width: { xs: 30, sm: 36 },
                      height: { xs: 30, sm: 36 },
                      borderRadius: '50%',
                      background: expandedMenus[index]
                        ? 'rgba(255,255,255,0.2)'
                        : 'rgba(59, 130, 246, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      transform: expandedMenus[index] ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      <ExpandMoreIcon />
                    </Box>
                  </Stack>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expandedMenus[index]} timeout={600} sx={{ transition: 'all 0.8s ease' }} easing="cubic-bezier(0.4, 0, 0.2, 1)">
                  <Box sx={{
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 1.5 },
                    pt: 0,
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f8fafc 100%)',
                    borderTop: '1px solid rgba(59, 130, 246, 0.1)'
                  }}>
                    <Divider sx={{ mb: { xs: 2, sm: 2.5 }, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                    <Stack spacing={{ xs: 1.5, sm: 2 }}>
                      {menu.actions.map((action, actionIndex) => (
                        <Card
                          key={actionIndex}
                          elevation={0}
                          sx={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                            border: '1px solid #e2e8f0',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'scale(1.005)',
                              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                              border: '1px solid rgba(59, 130, 246, 0.3)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                                <Box sx={{
                                  width: { xs: 36, sm: 40 },
                                  height: { xs: 36, sm: 40 },
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <SettingsIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#3b82f6' }} />
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '0.85rem', sm: '0.9375rem' },
                                    color: '#1e293b',
                                    mb: 0.5
                                  }}>
                                    {action.definition}
                                  </Typography>
                                  <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    color: '#64748b'
                                  }}>
                                    Rol atama ve yetkilendirme işlemi
                                  </Typography>
                                </Box>
                              </Stack>

                              {canManageRoles && (
                                <Button
                                  variant="contained"
                                  startIcon={!isMobile ? <AddIcon /> : null}
                                  onClick={() => {
                                    handleShowRoles(action.code, menu.name, action.definition);
                                  }}
                                  sx={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.9375rem',
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: { xs: 1.5, sm: 2.5 },
                                    py: 1,
                                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                                    minWidth: { xs: 'auto', sm: 120 },
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                                      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)'
                                    }
                                  }}
                                >
                                  {isMobile ? '+' : 'Rol Yönet'}
                                </Button>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
              </Card>
            </WaveAnimation>
          ))}
        </Stack>
      </Container>
  );
};

export default Authorizations;
